import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmtDate, formatApiError } from "@/lib/api";
import { useAssetClasses } from "@/lib/assetClasses";
import RoomPicker from "@/components/RoomPicker";
import { Paperclip, X, UploadSimple, FileImage, FilePdf, FileDoc, Star, Plus, Gear } from "@phosphor-icons/react";

const empty = {
  name: "",
  category: "other",
  brand: "",
  model: "",
  serial: "",
  purchase_date: "",
  purchase_price: "",
  fmv: "",
  rcv: "",
  location: "",
  sub_location: "",
  provenance: "",
  condition: "",
  dispensation: "",
  notes: "",
  photo_paths: [],
  document_paths: [],
  featured_photo_path: null,
  // Operational fields — meaningful only when category="home_component"
  component_type: "",
  room: "",
  install_date: "",
  warranty_expiry: "",
  material: "",
  paint_code: "",
  specs: "",
  service_history: "",
};

const COMPONENT_TYPES = [
  { value: "", label: "—" },
  { value: "roof", label: "Roof" },
  { value: "hvac", label: "HVAC" },
  { value: "windows", label: "Windows" },
  { value: "water_heater", label: "Water Heater" },
  { value: "paint", label: "Paint Code" },
  { value: "appliance", label: "Appliance" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
  { value: "flooring", label: "Flooring" },
  { value: "exterior", label: "Exterior / Siding" },
  { value: "landscape", label: "Landscape / Hardscape" },
  { value: "other", label: "Other" },
];

export default function AssetForm({ initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(empty);
  const [attachments, setAttachments] = useState([]); // [{path, original_filename, content_type, kind}]
  const [uploading, setUploading] = useState(false);
  const { classes: assetClasses, refresh: refreshClasses, classByKey } = useAssetClasses();
  const [showNewClass, setShowNewClass] = useState(false);
  const [newClassLabel, setNewClassLabel] = useState("");
  const [creatingClass, setCreatingClass] = useState(false);

  const categoryOptions = (assetClasses || []).length
    ? assetClasses.map((c) => ({ value: c.key, label: c.label }))
    : [{ value: "other", label: "Other" }];

  // Operational details surface for any class flagged has_operational_details
  // (system home_component, or any custom class the user marked).
  const activeClass = classByKey(form.category);
  const showOpDetails = !!activeClass?.has_operational_details;

  const createInlineClass = async () => {
    const label = newClassLabel.trim();
    if (!label) return;
    setCreatingClass(true);
    try {
      const r = await api.post("/asset-classes", { label });
      toast.success(`"${label}" added.`);
      setShowNewClass(false);
      setNewClassLabel("");
      await refreshClasses();
      setForm((f) => ({ ...f, category: r.data.key }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create class.");
    } finally {
      setCreatingClass(false);
    }
  };

  useEffect(() => {
    if (!initial) return;
    let cancelled = false;
    const merged = { ...empty };
    for (const k of Object.keys(empty)) {
      if (initial[k] !== undefined && initial[k] !== null) merged[k] = initial[k];
    }
    // Ensure arrays
    merged.photo_paths = Array.isArray(merged.photo_paths) ? merged.photo_paths : [];
    merged.document_paths = Array.isArray(merged.document_paths) ? merged.document_paths : [];
    setForm(merged);

    // Fetch attachment metadata to render nice rows
    const allPaths = [
      ...merged.photo_paths.map((p) => ({ path: p, kind: "photo" })),
      ...merged.document_paths.map((p) => ({ path: p, kind: "document" })),
    ];
    if (allPaths.length) {
      (async () => {
        try {
          const r = await api.get("/vault");
          if (cancelled) return;
          const byPath = Object.fromEntries(r.data.map((f) => [f.storage_path, f]));
          const rows = allPaths.map(({ path, kind }) => ({
            path,
            kind,
            original_filename: byPath[path]?.original_filename || path.split("/").pop(),
            content_type: byPath[path]?.content_type || (kind === "photo" ? "image/*" : "application/octet-stream"),
          }));
          setAttachments(rows);
        } catch {
          // Best-effort: existing UI handles missing metadata gracefully.
        }
      })();
    } else {
      setAttachments([]);
    }
    return () => { cancelled = true; };
  }, [initial]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onPick = async (e, kind) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newRows = [...attachments];
    const newPhotos = [...form.photo_paths];
    const newDocs = [...form.document_paths];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const f = res.data;
        newRows.push({
          path: f.storage_path,
          kind,
          original_filename: f.original_filename,
          content_type: f.content_type,
        });
        if (kind === "photo") newPhotos.push(f.storage_path);
        else newDocs.push(f.storage_path);
      } catch (err) {
        toast.error(`Upload failed for ${file.name}`);
      }
    }
    setAttachments(newRows);
    // First photo defaults to featured if no featured was set yet
    const featured =
      form.featured_photo_path && newPhotos.includes(form.featured_photo_path)
        ? form.featured_photo_path
        : newPhotos[0] || null;
    setForm({ ...form, photo_paths: newPhotos, document_paths: newDocs, featured_photo_path: featured });
    setUploading(false);
    e.target.value = "";
  };

  const setFeatured = (path) => {
    if (!form.photo_paths.includes(path)) return;
    setForm({ ...form, featured_photo_path: path });
  };

  const removeAttachment = async (path, kind) => {
    // Optimistic local update
    setAttachments(attachments.filter((a) => a.path !== path));
    if (kind === "photo") {
      const remaining = form.photo_paths.filter((p) => p !== path);
      const nextFeatured =
        form.featured_photo_path === path ? remaining[0] || null : form.featured_photo_path;
      setForm({ ...form, photo_paths: remaining, featured_photo_path: nextFeatured });
    } else {
      setForm({ ...form, document_paths: form.document_paths.filter((p) => p !== path) });
    }
    // Soft-delete the underlying file record (fire-and-forget)
    try {
      await api.delete(`/files/by-path/${path}`);
    } catch (err) {
      // Non-fatal — the asset save will proceed either way
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["purchase_price", "fmv", "rcv"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null) payload[k] = null;
      else payload[k] = parseFloat(payload[k]);
    });
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    payload.photo_paths = form.photo_paths;
    payload.document_paths = form.document_paths;
    payload.featured_photo_path = form.featured_photo_path;
    // Operational fields — only persist if the chosen class supports them
    if (!showOpDetails) {
      for (const k of ["component_type", "room", "install_date", "warranty_expiry",
                       "material", "paint_code", "specs", "service_history"]) {
        payload[k] = null;
      }
    }
    if (!payload.name) return;
    onSubmit(payload);
  };

  const photoRows = attachments.filter((a) => a.kind === "photo");
  const docRows = attachments.filter((a) => a.kind === "document" || a.kind === "appraisal" || a.kind === "manual");

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="asset-form">
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
        Ledger Entry · Formal Record
      </div>

      <SectionTitle>Identification</SectionTitle>
      <Grid>
        <Inp label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="form-name" className="md:col-span-2" />
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#5C5F58] font-mono-ledger flex-1">Class</label>
            {!showNewClass && (
              <button
                type="button"
                onClick={() => setShowNewClass(true)}
                data-testid="form-add-class"
                className="inline-flex items-center gap-1 text-[10px] font-mono-ledger uppercase tracking-[0.15em] text-[#1A3622] hover:text-[#0F2818]"
              >
                <Plus size={10} weight="bold" /> New class
              </button>
            )}
          </div>
          {showNewClass ? (
            <div className="flex gap-1" data-testid="form-new-class-row">
              <input
                type="text"
                value={newClassLabel}
                onChange={(e) => setNewClassLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), createInlineClass())}
                autoFocus
                placeholder="e.g. Sports Memorabilia"
                maxLength={50}
                data-testid="form-new-class-input"
                className="flex-1 bg-[#F9F8F6] border border-[#1A3622] px-3 py-2 text-sm focus:outline-none"
              />
              <button
                type="button"
                onClick={createInlineClass}
                disabled={creatingClass || !newClassLabel.trim()}
                data-testid="form-new-class-save"
                className="bg-[#1A3622] text-[#F9F8F6] px-2.5 py-2 text-xs border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
              >
                {creatingClass ? "…" : "Add"}
              </button>
              <button
                type="button"
                onClick={() => { setShowNewClass(false); setNewClassLabel(""); }}
                className="border border-[#E0DDD6] text-[#5C5F58] px-2 py-2 text-xs hover:border-[#5C5F58]"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ) : (
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              data-testid="form-category"
              className="w-full bg-[#F9F8F6] border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
            >
              {categoryOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">
            Room
          </label>
          <RoomPicker
            value={form.location}
            onChange={(v) => set("location", v)}
            testid="form-location"
          />
        </div>
        <Inp
          label="Where in the room? (optional)"
          value={form.sub_location}
          onChange={(v) => set("sub_location", v)}
          testid="form-sub-location"
          placeholder="e.g. top drawer of the dresser"
        />
        <Inp label="Brand" value={form.brand} onChange={(v) => set("brand", v)} testid="form-brand" />
        <Inp label="Model" value={form.model} onChange={(v) => set("model", v)} testid="form-model" />
        <Inp label="Serial Number" value={form.serial} onChange={(v) => set("serial", v)} testid="form-serial" mono />
        <Inp label="Condition" value={form.condition} onChange={(v) => set("condition", v)} placeholder="mint, excellent, fair, etc." />
      </Grid>

      <SectionTitle>Valuation</SectionTitle>
      <Grid>
        <Inp label="Purchase Date" type="date" value={form.purchase_date} onChange={(v) => set("purchase_date", v)} />
        <Inp label="Purchase Price (USD)" type="number" value={form.purchase_price} onChange={(v) => set("purchase_price", v)} />
        <Inp label="Fair Market Value" type="number" value={form.fmv} onChange={(v) => set("fmv", v)} testid="form-fmv" />
        <Inp label="Replacement Cost" type="number" value={form.rcv} onChange={(v) => set("rcv", v)} testid="form-rcv" />
      </Grid>

      <SectionTitle>Legacy</SectionTitle>
      <Grid>
        <Ta label="Provenance" value={form.provenance} onChange={(v) => set("provenance", v)} className="md:col-span-2" rows={2} />
        <Ta label="Dispensation Instructions (private — for executors)" value={form.dispensation} onChange={(v) => set("dispensation", v)} className="md:col-span-2" rows={3} />
        <Ta label="Notes" value={form.notes} onChange={(v) => set("notes", v)} className="md:col-span-2" rows={3} />
      </Grid>

      {showOpDetails && (
        <div data-testid="operational-details-section">
          <SectionTitle>Operational Details</SectionTitle>
          <Grid>
            <Sel
              label="Component Type"
              value={form.component_type || ""}
              onChange={(v) => set("component_type", v)}
              options={COMPONENT_TYPES}
              testid="form-component-type"
            />
            <Inp label="Room" value={form.room || ""} onChange={(v) => set("room", v)} testid="form-room" />
            <Inp label="Install Date" type="date" value={form.install_date || ""} onChange={(v) => set("install_date", v)} testid="form-install-date" />
            <Inp label="Warranty Expires" type="date" value={form.warranty_expiry || ""} onChange={(v) => set("warranty_expiry", v)} testid="form-warranty-expiry" />
            <Inp label="Material" value={form.material || ""} onChange={(v) => set("material", v)} testid="form-material" />
            <Inp label="Paint / Finish Code" value={form.paint_code || ""} onChange={(v) => set("paint_code", v)} mono testid="form-paint-code" />
            <Ta label="Specs" value={form.specs || ""} onChange={(v) => set("specs", v)} className="md:col-span-2" rows={2} testid="form-specs" />
            <Ta label="Service History" value={form.service_history || ""} onChange={(v) => set("service_history", v)} className="md:col-span-2" rows={3} testid="form-service-history" />
          </Grid>
        </div>
      )}

      <SectionTitle>Attachments</SectionTitle>
      <div className="space-y-4" data-testid="attachments-section">
        <div className="flex flex-wrap gap-2">
          <label
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs border border-[#1A3622] text-[#1A3622] bg-white cursor-pointer hover:bg-[#1A3622] hover:text-[#F9F8F6] ${uploading ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="attach-photo-btn"
          >
            <FileImage size={14} /> Attach Photos
            <input type="file" accept="image/*" multiple onChange={(e) => onPick(e, "photo")} className="hidden" />
          </label>
          <label
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs border border-[#0A192F] text-[#0A192F] bg-white cursor-pointer hover:bg-[#0A192F] hover:text-[#F9F8F6] ${uploading ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="attach-doc-btn"
          >
            <Paperclip size={14} /> Attach Documents
            <input type="file" multiple onChange={(e) => onPick(e, "document")} className="hidden" />
          </label>
          {uploading && <span className="text-xs text-[#5C5F58] self-center">Uploading…</span>}
        </div>

        {photoRows.length > 0 && (
          <div className="border border-[#E0DDD6] bg-[#F9F8F6] p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Photos · {photoRows.length}
              </div>
              <div className="font-mono-ledger text-[9px] uppercase tracking-[0.2em] text-[#5C5F58] italic">
                Click <Star size={9} weight="fill" className="inline -mt-0.5" /> to set the thumbnail
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-grid">
              {photoRows.map((a) => {
                const isFeatured = form.featured_photo_path === a.path;
                return (
                  <div
                    key={a.path}
                    className={`relative group bg-white border ${isFeatured ? "border-[#C5A059] ring-2 ring-[#C5A059]/40" : "border-[#E0DDD6]"} aspect-square overflow-hidden`}
                  >
                    <img
                      src={`${API}/files/${a.path}`}
                      alt={a.original_filename}
                      className="w-full h-full object-cover"
                      data-testid={`photo-thumb-${a.path}`}
                    />
                    {/* Featured pill — always visible when this photo is the feature */}
                    {isFeatured && (
                      <div
                        className="absolute bottom-1 left-1 inline-flex items-center gap-1 bg-[#C5A059] text-[#0A192F] px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em]"
                        data-testid={`featured-pill-${a.path}`}
                      >
                        <Star size={8} weight="fill" /> Featured
                      </div>
                    )}
                    {/* Star toggle — bottom right, always visible (filled for featured, outline otherwise) */}
                    <button
                      type="button"
                      onClick={() => setFeatured(a.path)}
                      data-testid={`set-featured-${a.path}`}
                      title={isFeatured ? "This is the featured thumbnail" : "Set as featured thumbnail"}
                      className={`absolute top-1 left-1 w-6 h-6 flex items-center justify-center transition-colors ${
                        isFeatured
                          ? "bg-[#C5A059] text-[#0A192F]"
                          : "bg-[#0A192F]/70 text-white opacity-0 group-hover:opacity-100 hover:bg-[#C5A059] hover:text-[#0A192F]"
                      }`}
                      aria-label={isFeatured ? "Featured photo" : "Set as featured photo"}
                    >
                      <Star size={12} weight={isFeatured ? "fill" : "regular"} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.path, "photo")}
                      data-testid={`remove-photo-${a.path}`}
                      className="absolute top-1 right-1 w-6 h-6 bg-[#0A192F]/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove photo"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {docRows.length > 0 && (
          <div className="border border-[#E0DDD6] bg-[#F9F8F6] p-3">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
              Documents · {docRows.length}
            </div>
            <ul className="divide-y divide-[#E0DDD6] bg-white border border-[#E0DDD6]" data-testid="doc-list">
              {docRows.map((a) => {
                const Icon = a.content_type === "application/pdf" ? FilePdf : FileDoc;
                const color = a.content_type === "application/pdf" ? "#6B1718" : "#5C5F58";
                return (
                  <li key={a.path} className="px-3 py-2 flex items-center gap-3">
                    <Icon size={18} color={color} />
                    <a
                      href={`${API}/files/${a.path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#0A192F] hover:underline flex-1 truncate"
                    >
                      {a.original_filename}
                    </a>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.path, "document")}
                      data-testid={`remove-doc-${a.path}`}
                      className="text-[#5C5F58] hover:text-[#6B1718]"
                      aria-label="Remove document"
                    >
                      <X size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {photoRows.length === 0 && docRows.length === 0 && !uploading && (
          <p className="text-xs text-[#5C5F58] italic">
            No attachments yet. Add product photos, serial-plate shots, receipts, appraisals, or owner&apos;s manuals.
          </p>
        )}
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E0DDD6]">
        <button
          data-testid="submit-asset"
          type="submit"
          className="bg-[#1A3622] text-[#F9F8F6] px-6 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818]"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function SectionTitle({ children }) {
  return <h3 className="font-serif-display text-2xl text-[#0A192F] border-b border-[#E0DDD6] pb-2">{children}</h3>;
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Inp({ label, value, onChange, type = "text", className = "", testid, placeholder, mono }) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622] ${mono ? "font-mono-ledger" : ""}`}
      />
    </label>
  );
}

function Ta({ label, value, onChange, className = "", rows = 3, testid }) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <textarea
        data-testid={testid}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
      />
    </label>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
      >
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </label>
  );
}
